<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubSheet from './Sheet.vue';
import { scopeWpHtml } from '@/utils/scopeWpHtml';
import { executeWpScripts } from '@/utils/executeWpScripts';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';
import { fetchBlogPostHandler } from '~/handlers/fetch-blog-post.handler';
import {
  mapPostToWp,
  type WpStylePost,
} from '~/utils/apostatudo/map-core-to-wp';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';

// A promoção do blog aberta DENTRO de um modal, empilhado por cima do hub Club
// Fidelidade — em vez de fechar o hub e navegar pra /blog/<slug>.
//
// O casco (painel da roletinha, topbar, animação, área que rola) é o Sheet.vue,
// compartilhado com o RewardsSheet. Aqui fica só o conteúdo: o MESMO post da
// página de blog — mesmo handler, mesmo `mapPostToWp`, mesmas classes
// (`blog-content-sandbox` + `cactus-blog content wp-content`) e mesmo par
// scopeWpHtml/executeWpScripts. A folha de estilo do conteúdo vive em
// assets/css/blog-content.scss (importada em main.scss) justamente por ter dois
// consumidores agora.
//
// Estado só em memória (nada na URL), igual roletinha e cashback.
const store = useLoyaltyHubStore();
const { postSlug } = storeToRefs(store);

const { blogPostClickEvent } = useUserJourney();

const isLoading = ref(false);
const post = ref<WpStylePost | null>(null);

const contentRef = ref<HTMLElement | null>(null);

let cleanupWpScripts: (() => void) | null = null;
let cleanupLinks: (() => void) | null = null;

function close() {
  store.closePost();
}

// Links DENTRO do post: o CTA da promoção aponta pra uma rota interna, e sem
// intercepto a navegação acontecia EMBAIXO do modal (que segue montado por cima
// da rota nova, junto com o hub). Fecha os dois e navega.
// Link externo, `target=_blank`, âncora (#) e clique com modificador/botão do
// meio seguem o comportamento nativo.
function interceptLinks(container: HTMLElement): () => void {
  function handleClick(event: MouseEvent) {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const anchor = (event.target as HTMLElement | null)?.closest('a');

    if (!anchor || !container.contains(anchor)) return;

    const href = anchor.getAttribute('href') ?? '';

    if (!href || href.startsWith('#')) return;
    if (anchor.getAttribute('target') === '_blank') return;

    let url: URL;

    try {
      url = new URL(href, window.location.origin);
    } catch {
      return;
    }

    // Só rota interna: externo sai do SPA e não tem o que fechar.
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // `store.close()` já limpa o `postSlug` — fecha hub e modal de uma vez.
    store.close();
    navigateTo(`${url.pathname}${url.search}${url.hash}`);
  }

  document.addEventListener('click', handleClick, true);

  return () => document.removeEventListener('click', handleClick, true);
}

function teardownContent() {
  cleanupWpScripts?.();
  cleanupLinks?.();
  cleanupWpScripts = null;
  cleanupLinks = null;
}

async function load(slug: string) {
  isLoading.value = true;
  post.value = null;

  try {
    const { post: detail } = await fetchBlogPostHandler(slug);

    post.value = detail
      ? mapPostToWp(detail, [], detail.html_content || '')
      : null;

    if (detail) blogPostClickEvent(slug);
  } catch (error) {
    console.error('[LoyaltyHubPostSheet] falha ao carregar o post', error);
    post.value = null;
  } finally {
    isLoading.value = false;
  }
}

// `immediate` porque o deep-link (`?open=post&slug=…`) é lido no `onMounted` do
// app.vue, e o `<LoyaltyHub>` que traz este sheet é async no layout: quando o
// chunk chega, o `postSlug` JÁ está no store e o watch nunca dispararia — o
// painel subia vazio. Pelo card do hub o componente já está montado e o caminho
// é o mesmo.
watch(
  postSlug,
  (slug) => {
    if (slug) load(slug);
  },
  { immediate: true },
);

// Os scripts autorais do HTML do CMS (e o intercepto de links) precisam do DOM
// já pintado — mesma mecânica do Core.vue, com `flush: 'post'`.
watch(
  () => post.value?.content?.rendered,
  async (content) => {
    teardownContent();

    if (!content) return;

    await nextTick();

    if (!contentRef.value) return;

    cleanupWpScripts = executeWpScripts(contentRef.value);
    cleanupLinks = interceptLinks(contentRef.value);
  },
  { flush: 'post' },
);

function onClosed() {
  // Solta o conteúdo só no fim do slide-down: descarregar antes fazia o painel
  // descer vazio.
  teardownContent();
  post.value = null;
}

onUnmounted(teardownContent);
</script>

<template>
  <!-- O HTML do CMS é autorado pra largura da página de blog (o container do
       layout é max-w-[1440px] com px-4). Nos 860px padrão do casco os blocos do
       post apertam/quebram, então o painel acompanha a mesma régua no desktop,
       com folga lateral pra continuar lendo como modal sobreposto. -->
  <LoyaltyHubSheet
    desktop-max-width="min(1440px, calc(100vw - 48px))"
    mobile-content-padding-x="0px"
    :open="!!postSlug"
    @close="close"
    @closed="onClosed"
  >
    <div
      v-if="isLoading"
      class="lhp-loading"
    >
      <div
        class="load skeleton-loading"
        style="height: 220px"
      ></div>
      <div
        v-for="(_n, index) in 12"
        :key="`lhp-skeleton-${index}`"
        class="load skeleton-loading"
        style="height: 16px; margin: 14px 0"
      ></div>
    </div>

    <div
      v-else-if="post?.content?.rendered"
      ref="contentRef"
      class="cactus-blog content wp-content"
    >
      <div
        class="blog-content-sandbox"
        v-html="scopeWpHtml(post.content.rendered, '.blog-content-sandbox')"
      ></div>
    </div>

    <div
      v-else
      class="alert alert-warning"
    >
      <span>{{ $t('no_posts_found') }}</span>
    </div>
  </LoyaltyHubSheet>
</template>

<style scoped lang="scss">
.lhp-loading .load {
  @apply w-full rounded overflow-hidden;
}
</style>
